import React, { useState, useEffect, useCallback } from 'react';
import '../../css/Pages.css';
import WorkHistoryListing from '../../components/Profile/WorkHistory/WorkHistoryListing';
import WorkHistoryForm from '../../components/Profile/WorkHistory/WorkHistoryForm';
import { useConfirmDeleteDialog } from '../../hooks/useConfirmDeleteDialog';

const WorkHistory = () => {
    const initialForm = { StartDate: '', EndDate: '', Workplace: '', Position: '', StartDateAccuracy: 'date', EndDateAccuracy: 'date' };

    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');

    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formData, setFormData] = useState(initialForm);
    const [editId, setEditId] = useState(null);

    const { confirmDeleteDialog } = useConfirmDeleteDialog();

    const getAuthHeaders = useCallback(() => {
        return { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };
    }, []);

    const handleLogout = useCallback(() => {
        alert("Phiên đăng nhập đã hết hạn hoặc bạn không có quyền truy cập. Vui lòng đăng nhập lại!");
        localStorage.removeItem('user');
        localStorage.removeItem('accessToken');
        window.location.href = '/login';
    }, []);

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const currentUser = JSON.parse(localStorage.getItem('user'));
            if (!currentUser || !currentUser.Id) {
                handleLogout();
                return;
            }

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/workhistory?userId=${currentUser.Id}`, {
                headers: getAuthHeaders()
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                const result = await response.json();
                setData(result);
                setFilteredData(result);
            }
        } catch (error) {
            console.error("Lỗi fetch data:", error);
        } finally {
            setIsLoading(false);
        }
    }, [getAuthHeaders, handleLogout]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        setFilteredData(data.filter(item =>
            item.Workplace?.toLowerCase().includes(query) ||
            item.Position?.toLowerCase().includes(query)
        ));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.StartDate && formData.EndDate) {
            const start = new Date(formData.StartDate);
            const end = new Date(formData.EndDate);
            if (end < start) {
                alert("Ngày kết thúc không được nhỏ hơn ngày bắt đầu!");
                return;
            }
        }

        const method = editId ? 'PUT' : 'POST';
        const currentUser = JSON.parse(localStorage.getItem('user'));

        const payload = {
            ...formData,
            UserId: currentUser.Id
        };

        if (editId) payload.Id = editId;

        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/workhistory`, {
                method,
                headers: {
                    ...getAuthHeaders(),
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (response.status === 401 || response.status === 403) {
                handleLogout();
                return;
            }

            if (response.ok) {
                fetchData();
                closeModal();
            } else {
                alert("Có lỗi xảy ra khi lưu dữ liệu!");
            }
        } catch (error) { console.error("Lỗi submit:", error); }
    };

    const handleEdit = (item) => {
        setEditId(item.Id);
        setFormData({
            StartDate: item.StartDate || '',
            EndDate: item.EndDate || '',
            Workplace: item.Workplace || '',
            Position: item.Position || '',
            StartDateAccuracy: item.StartDateAccuracy || 'date',
            EndDateAccuracy: item.EndDateAccuracy || 'date'
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        confirmDeleteDialog({
            header: 'Xác nhận xóa',
            message: 'Bạn có chắc chắn muốn xóa quá trình công tác này?',
            accept: async () => {
                const currentUser = JSON.parse(localStorage.getItem('user'));
                const userId = currentUser ? currentUser.Id : null;

                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/workhistory?id=${id}&userId=${userId}`, {
                    method: 'DELETE',
                    headers: getAuthHeaders()
                });

                if (response.status === 401 || response.status === 403) {
                    handleLogout();
                    return;
                }

                if (response.ok) {
                    fetchData();
                } else {
                    alert("Có lỗi xảy ra khi xóa dữ liệu!");
                }
            }
        });
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setFormData(initialForm);
        setEditId(null);
    };

    return (
        <div className="page-container" style={{ backgroundColor: '#f4f6f9', minHeight: '100vh' }}>
            <div className="page-header mb-4">
                <div className="header-title">
                    <h2 style={{ textTransform: 'uppercase' }}>QUÁ TRÌNH CÔNG TÁC</h2>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' }}>
                <button className="btn-add-new" onClick={() => setIsModalOpen(true)} style={{ margin: 0 }}>
                    <i className="fa-solid fa-plus"></i> Thêm mới
                </button>

                <div style={{ position: 'relative', width: '100%', maxWidth: '350px' }}>
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Tìm kiếm"
                        value={searchQuery}
                        onChange={handleSearch}
                    />
                    <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', right: '15px', top: '12px', color: '#999' }}></i>
                </div>
            </div>

            <WorkHistoryListing
                data={filteredData}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isLoading={isLoading}
            />

            <WorkHistoryForm
                isOpen={isModalOpen}
                onClose={closeModal}
                onSubmit={handleSubmit}
                formData={formData}
                setFormData={setFormData}
                isEditing={!!editId}
            />
        </div>
    );
};

export default WorkHistory;